<script lang="ts">
  /* UpgradeModal — what a locked door says when it is knocked on.

     Opens whenever the app dispatches UPGRADE_REQUIRED_EVENT: automatically
     (api.ts on a 402 UPGRADE_REQUIRED, whose details name the feature) or
     proactively (requestUpgrade() from a locked control). It names the item
     that opens the door and sends the owner to the constructor with that item
     ticked — the company subscribes itself; there is nobody to write to.
     Mounted once in AppShell. */
  import { onMount } from "svelte";
  import { _ } from "svelte-i18n";
  import { push } from "svelte-spa-router";
  import { UPGRADE_REQUIRED_EVENT } from "$lib/api";
  import { auth } from "$lib/auth";
  import { membershipFor } from "$lib/permissions";
  import {
    FEATURE_CODE, ITEM_DESC_KEY, ITEM_NAME_KEY, SUBSCRIBABLE_CODES, isGateShown,
    type ItemCode, type PaidFeature,
  } from "$lib/billing";

  let { tenantSlug }: { tenantSlug: string } = $props();

  let open = $state(false);
  let code = $state<ItemCode>("base");

  /** The legacy names a few call sites still pass ("orders", "pricing"). */
  function codeFor(feature?: string, item?: string): ItemCode {
    if (item && SUBSCRIBABLE_CODES.includes(item)) return item as ItemCode;
    return FEATURE_CODE[feature as PaidFeature] ?? "base";
  }

  onMount(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      // A lock screen for this very feature is already on the page.
      if (isGateShown(detail.feature)) return;
      code = codeFor(detail.feature, detail.item);
      open = true;
    };
    window.addEventListener(UPGRADE_REQUIRED_EVENT, handler);
    return () => window.removeEventListener(UPGRADE_REQUIRED_EVENT, handler);
  });

  const owner = $derived(membershipFor($auth, tenantSlug)?.role === "owner");
  const isBase = $derived(code === "base");

  function close() {
    open = false;
  }

  function toConstructor() {
    open = false;
    push(`/t/${tenantSlug}/pricing?add=${code}`);
  }
</script>

{#if open}
  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
  <div class="up-backdrop" role="presentation" onclick={close}>
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="up-modal" role="dialog" aria-modal="true" tabindex="-1" onclick={(e) => e.stopPropagation()}>
      <div class="up-header">
        <span class="material-icons-outlined up-lock" aria-hidden="true">lock</span>
        <h2 class="up-title">
          {isBase ? $_("upgrade.locked.pricing.title") : $_(ITEM_NAME_KEY[code])}
        </h2>
        <button class="up-x" onclick={close} aria-label={$_("common.close")}>
          <span class="material-icons-outlined">close</span>
        </button>
      </div>

      <div class="up-body">
        <p class="up-lead">{$_(ITEM_DESC_KEY[code])}</p>
        <p class="up-note">{isBase ? $_("billing.gate.baseBody") : $_("billing.gate.lockBody")}</p>
        {#if !owner}
          <p class="up-note">{$_("billing.gate.askOwner")}</p>
        {/if}
      </div>

      <div class="up-footer">
        <button class="btn btn--ghost" onclick={close}>{$_("upgrade.close")}</button>
        {#if owner}
          <button class="up-cta" onclick={toConstructor}>{$_("billing.gate.cta")}</button>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .up-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 2200; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .up-modal { background: #fff; border-radius: 12px; width: 100%; max-width: 480px; box-shadow: 0 24px 72px rgba(0,0,0,0.28); display: flex; flex-direction: column; overflow: hidden; }
  .up-header { display: flex; align-items: center; gap: 12px; padding: 22px 24px 14px; border-bottom: 1px solid #eee; }
  .up-lock { color: var(--color-primary); font-size: 22px; flex-shrink: 0; }
  .up-title { margin: 0; font-size: 18px; font-weight: 700; color: #1f2733; flex: 1; }
  .up-x { background: none; border: none; cursor: pointer; color: #9aa3ad; display: flex; padding: 2px; }
  .up-x:hover { color: #555; }
  .up-body { padding: 18px 24px; display: flex; flex-direction: column; gap: 10px; }
  .up-lead { margin: 0; font-size: 14px; color: #4a525c; line-height: 1.55; }
  .up-note { margin: 0; font-size: 12.5px; color: #8a929b; line-height: 1.5; }
  .up-footer { padding: 14px 24px; border-top: 1px solid #eee; display: flex; justify-content: flex-end; gap: 10px; background: #fafbfc; flex-wrap: wrap; }
  .up-cta { background: var(--color-primary); color: #fff; border: none; padding: 10px 22px; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit; }
  .up-cta:hover { background: var(--color-primary-hover); }
</style>
